import { useEffect, useMemo, useState } from 'react';
import { CloseIcon } from '@icons/CloseIcon';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@hooks/useTranslation';
import type { EditableImage } from '@hooks/useImageEditor';
import { DropzoneUpload } from './ImageUpload';

const REMOVE_BADGE_CLASSES =
  'absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-dinnerplate-white text-grey';

interface UploadImagesModalProps {
  open: boolean;
  existingImages: EditableImage[];
  newFiles: File[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (files: File[]) => void;
  onRemoveExisting: (signedId: string) => void;
  onRemoveNewFile: (index: number) => void;
}

export function UploadImagesModal({
  open,
  existingImages,
  newFiles,
  onOpenChange,
  onConfirm,
  onRemoveExisting,
  onRemoveNewFile,
}: UploadImagesModalProps) {
  const { t } = useTranslation();
  const [staged, setStaged] = useState<File[]>([]);

  useEffect(() => {
    if (!open) setStaged([]);
  }, [open]);

  const newFileUrls = useMemo(() => newFiles.map((f) => URL.createObjectURL(f)), [newFiles]);
  useEffect(() => () => newFileUrls.forEach((url) => URL.revokeObjectURL(url)), [newFileUrls]);

  const stagedUrls = useMemo(() => staged.map((f) => URL.createObjectURL(f)), [staged]);
  useEffect(() => () => stagedUrls.forEach((url) => URL.revokeObjectURL(url)), [stagedUrls]);

  const existingCount = existingImages.length + newFiles.length;
  const hasThumbnails = existingCount + staged.length > 0;

  const handleClose = () => onOpenChange(false);

  const handleConfirm = () => {
    if (staged.length === 0) return;
    onConfirm(staged);
    onOpenChange(false);
  };

  const title = t('common.images.upload_modal.title');
  const isDisabled = staged.length === 0;

  const footer = (
    <div className="flex w-full items-center justify-end">
      <Button
        disabled={isDisabled}
        onClick={handleConfirm}
      >
        {t('common.images.upload_modal.upload')}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onEscapeKeyDown={handleClose}
        aria-label={title}
        className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
      >
        <ModalLayout
          title={<ModalTitle>{title}</ModalTitle>}
          onClose={handleClose}
          footer={footer}
          contentClassName="![scrollbar-gutter:auto]"
        >
          <DropzoneUpload
            files={staged}
            onFilesChange={setStaged}
            existingCount={existingCount}
            hidePreview
          />
          {hasThumbnails && (
            <div className="mt-4 flex flex-wrap gap-3">
              {existingImages.map((image) => (
                <div key={image.signed_id} className="relative">
                  <div className="h-24 w-24 overflow-hidden rounded-input border border-dinnerplate-white bg-dinnerplate-white">
                    <img src={image.url} alt={image.filename} className="h-full w-full object-contain" />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${image.filename}`}
                    onClick={() => onRemoveExisting(image.signed_id)}
                    className={REMOVE_BADGE_CLASSES}
                  >
                    <CloseIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {newFiles.map((file, index) => (
                <div key={`new-${index}`} className="relative">
                  <div className="h-24 w-24 overflow-hidden rounded-input border border-dinnerplate-white bg-dinnerplate-white">
                    <img src={newFileUrls[index]} alt={file.name} className="h-full w-full object-contain" />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => onRemoveNewFile(index)}
                    className={REMOVE_BADGE_CLASSES}
                  >
                    <CloseIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {staged.map((file, index) => (
                <div key={`staged-${index}`} className="relative">
                  <div className="h-24 w-24 overflow-hidden rounded-input border border-dinnerplate-white bg-dinnerplate-white">
                    <img src={stagedUrls[index]} alt={file.name} className="h-full w-full object-contain" />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setStaged(staged.filter((_, i) => i !== index))}
                    className={REMOVE_BADGE_CLASSES}
                  >
                    <CloseIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </ModalLayout>
      </DialogContent>
    </Dialog>
  );
}
